import { Link } from "react-router-dom"
import { useGetPurchaseQuery } from "shared/redux/endpoints/procurementPurchases"
import { Drawer, StatusPill, MoneyText, DateText, EmptyState, ProgressDots } from "procurement/design"
import { Loading } from "../../ui"
import en from "procurement/design/i18n/en"
import { canEditPurchase, PO_STATUS } from "../../status"

const PO_STEPS = [
  { key: "issued", label: en.purchases.issued },
  { key: "receiving", label: en.purchases.receiving },
  { key: "completed", label: en.purchases.completed },
]

const PurchaseQuickDrawer = ({ purchaseId, open, onClose }) => {
  const { data: po, isFetching } = useGetPurchaseQuery(purchaseId, { skip: !open || !purchaseId })
  const selectedProposal = po?.vendorProposals?.find((p) => p.isSelected)
  const supplierName = selectedProposal?.supplierName || po?.supplierName || po?.vendorName
  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={
        po ? (
          <div>
            <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{po.poCode}</div>
            <h2 className="pc-h1" style={{ fontSize: 18 }}>
              {supplierName || en.purchases.drawerTitle}
            </h2>
          </div>
        ) : (
          en.purchases.drawerTitle
        )
      }
      footer={
        purchaseId && (
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            {canEditPurchase(po) && (
              <Link to={`/procurement/purchases/${purchaseId}/edit`} className="pc-btn pc-btn--secondary pc-btn--sm">
                {po.status === PO_STATUS.INFO_REQUESTED ? "Edit & Resubmit" : "Edit"}
              </Link>
            )}
            <Link to={`/procurement/purchases/${purchaseId}`} className="pc-btn pc-btn--primary pc-btn--sm">
              {en.common.openFullPage}
            </Link>
          </div>
        )
      }
    >
      {isFetching && <Loading />}
      {!isFetching && !po && <EmptyState title="Couldn't load this purchase" />}
      {!isFetching && po && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <StatusPill status={po.status} />
            {po.paidStatus && <StatusPill status={po.paidStatus} />}
            <MoneyText value={po.total ?? po.totalAmount} align="left" />
          </div>
          <ProgressDots steps={PO_STEPS} current={po.status} />
          <dl style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, margin: 0, fontSize: 13 }}>
            <div>
              <dt style={{ color: "var(--text-muted)", fontSize: 12 }}>Supplier</dt>
              <dd style={{ margin: 0 }}>{supplierName || "—"}</dd>
            </div>
            <div>
              <dt style={{ color: "var(--text-muted)", fontSize: 12 }}>Created</dt>
              <dd style={{ margin: 0 }}>
                <DateText value={po.createdAt} withTime />
              </dd>
            </div>
          </dl>
          {!!po.linkedOrders?.length && (
            <section>
              <h3 style={{ fontSize: 13, margin: "0 0 8px" }}>Linked orders</h3>
              {po.linkedOrders.map((o) => (
                <div key={o.id} style={{ fontSize: 13, padding: "6px 0", borderTop: "1px solid var(--border)" }}>
                  {o.code || o.orCode} · {o.title || "—"}
                </div>
              ))}
            </section>
          )}
          {!!po.grn?.length && (
            <section>
              <h3 style={{ fontSize: 13, margin: "0 0 8px" }}>Goods receipt</h3>
              {po.grn.map((g, i) => (
                <div key={g.id || i} style={{ fontSize: 13, padding: "6px 0", borderTop: "1px solid var(--border)" }}>
                  {g.code || "GRN"} · <DateText value={g.createdAt} />
                </div>
              ))}
            </section>
          )}
          {/* TODO(BE): dedicated GRN list on GET /purchases/{id} — using whatever is present */}
          {!!po.paymentDetail?.length && (
            <section>
              <h3 style={{ fontSize: 13, margin: "0 0 8px" }}>Payment batches</h3>
              {po.paymentDetail.map((b, i) => (
                <div key={b.id || i} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "6px 0", borderTop: "1px solid var(--border)" }}>
                  <span>{b.batchLabel || b.paymentType || `Batch ${i + 1}`}</span>
                  <MoneyText value={b.amount} />
                </div>
              ))}
            </section>
          )}
        </div>
      )}
    </Drawer>
  )
}

export default PurchaseQuickDrawer
